import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

export type Item = {
  by: string;
  descendants: number;
  id: number;
  kids: number[];
  score: number;
  text: string;
  time: number;
  title: string;
  type: string;
  deleted: boolean;
  dead: boolean;
  parent: number;
  fetchedKids: Item[];
};

export default class Client {
  static async getArticles() {
    const stories = await this.request({ url: "/askstories.json" });

    return Promise.all(
      // TODO: allow infinite query
      stories.slice(0, 20).map((story: number) => {
        return this.getArticleFromId(story);
      })
    );
  }

  static getArticleFromId(id: number): Promise<Item> {
    return this.request({ url: `/item/${id}.json`, method: "GET" });
  }

  static getCommentFromId(id: number): Promise<Item> {
    return this.request({ url: `/item/${id}.json`, method: "GET" });
  }

  static getUser(author: string | undefined) {
    if (typeof author === "undefined") {
      return Promise.reject(new Error("Invalid author"));
    }
    return this.request({ url: `/user/${author}.json`, method: "GET" });
  }

  static async getAllComments(articleKids: number[]): Promise<Item[]> {
    const fetchCommentsAndAttachKids = async (parent: Item): Promise<void> => {
      if (!parent?.kids?.length) {
        return;
      }
      // fetch child comments
      const childComments = await Promise.all(
        parent.kids.map((id: number) => this.getCommentFromId(id))
      );

      // attech them to the parent comment
      parent.fetchedKids = childComments;

      // process each child comment recursively
      await Promise.all(
        childComments.map((childComment) =>
          fetchCommentsAndAttachKids(childComment)
        )
      );
    };

    // fetch top-level comments
    const rootComments = await Promise.all(
      articleKids.map((id) => this.getCommentFromId(id))
    );

    // process each top-level comment recursively
    await Promise.all(
      rootComments.map((comment) => fetchCommentsAndAttachKids(comment))
    );

    return rootComments;
  }

  private static async request(config: AxiosRequestConfig) {
    const client = (() => {
      return axios.create({
        baseURL: "https://hacker-news.firebaseio.com/v0",
      });
    })();

    const onSuccess = (response: AxiosResponse<any>) => {
      const { data } = response;
      return data;
    };

    const onError = (error: any) => {
      return Promise.reject(error.response);
    };

    return client(config).then(onSuccess).catch(onError);
  }
}
